import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getPostClosingFields, updatePostClosingFields } from 'services'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs } from './constants'

export function CollateralReview() {
  const [changed, setChanged] = useState(false)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const [isLoading, setLoading] = useState(false)

  const withNoteKey = (key: string) => `${key}Notes`

  const baseInputs = useMemo(() => defaultInputs(), [])

  const augmentedInputs = useMemo(() => {
    const nInputs: Record<string, any> = cloneDeep(baseInputs)
    Object.keys(baseInputs).forEach((key) => {
      const input = baseInputs[key]
      if (!input || input.inputType === 'section') return
      const noteKey = withNoteKey(key)
      nInputs[noteKey] = nInputs[noteKey] || { inputType: 'text', title: 'Notes' }
    })
    return nInputs
  }, [baseInputs])

  // baseKeys no longer used; rendering follows baseInputs key order

  useEffect(() => {
    const currentLoanNumber = setLoanNumber()
    const nInputs: Record<string, any> = cloneDeep(augmentedInputs)
    if (currentLoanNumber && !nInputs.LoanNumber?.value) {
      nInputs.LoanNumber = { ...(nInputs.LoanNumber || {}), value: currentLoanNumber, readOnly: true }
    }
    setInputs(nInputs)

    setLoading(true)
    getPostClosingFields('collateralReview')
      .then((data) => {
        const hydrated = cloneDeep(nInputs)
        Object.keys(hydrated).forEach((key) => {
          hydrated[key].value = data[key]
        })
        if (!hydrated.LoanNumber?.value && currentLoanNumber) {
          hydrated.LoanNumber = { ...(hydrated.LoanNumber || {}), value: currentLoanNumber, readOnly: true }
        }
        setInputs(hydrated)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [augmentedInputs])

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    const converted = InputConvert(newInputs[key], value)
    newInputs[key].value = converted
    newInputs[key].error = InputValidate(newInputs[key])
    setInputs(newInputs)
    setChanged(true)
  }

  const onSave = async () => {
    const payload: Record<string, any> = {}
    Object.keys(inputs).forEach((key) => {
      payload[key] = inputs[key].value
    })
    setLoading(true)
    setChanged(false)
    try {
      await updatePostClosingFields('collateralReview', payload)
      toast('Successfully updated', { type: 'info' })
    } catch (e) {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="collateral-review-container">
      <SaveChanges onSave={onSave} show={changed} />
      <div className="max-w-screen-2xl m-auto">
        <div className="bg-white">
          <div className="grid gap-5 md:grid-cols-2 grid-cols-1">
            <LayoutLoading show={isLoading} />

            {Object.keys(baseInputs).map((key) => {
              const input = inputs[key]
              if (!input) return null
              if (input.inputType === 'section') {
                return (
                  <div key={key} className="md:col-span-2">
                    <RenderInput input={{ ...input }} Key={key} onChange={onChange} />
                  </div>
                )
              }
              const noteKey = withNoteKey(key)
              return (
                <div key={key} className="md:col-span-2">
                  <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                    <div>
                      <RenderInput input={{ ...input }} Key={key} onChange={onChange} />
                    </div>
                    <div>
                      <RenderInput input={{ ...inputs[noteKey] }} Key={noteKey} onChange={onChange} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
